import { useState, useMemo, useCallback } from 'react';
import { SPACING } from '@/design-system';
import { cn } from '@/lib/utils';
import { lazy, Suspense } from 'react';
const TaxExplorerMap = lazy(() => import('./TaxExplorerMap').then(m => ({ default: m.TaxExplorerMap })));
import { TaxExplorerComparison } from './TaxExplorerComparison';
import { useLocationsWithCoordinates } from '@/hooks/useLocationsWithCoordinates';
import { calculateAllLocations, calculateCurrentTax, rankBestForYou } from '@/lib/taxCalculator';
import { ScenarioPanel } from './ScenarioPanel';
import { BestForYouList } from './BestForYouList';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { SlidersHorizontal } from 'lucide-react';
import { LComponents } from '@/components/i18n';
import { UserScenario, LocationWithCoordinates } from '@/types/taxExplorer';
import { Loader2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

const defaultScenario: UserScenario = {
  currentResidencyId: null,
  citizenshipIds: [],
  grossIncome: 100000,
  incomeType: 'employment',
  capitalGains: 0,
  dividends: 0,
  netWorth: 500000,
  plannedDays: 183,
  primaryGoal: 'minimize-tax',
  taxPriorities: [],
  lifestylePrefs: [],
  timeline: 'exploring',
  residencyType: 'permanent',
  incomeSourceType: 'same-as-residence',
  incomeSourceCountryIds: [],
  existingStructures: [],
  structureCountryIds: [],
  familyStatus: 'single',
  dependents: 0,
  industry: 'other'
};

interface TaxExplorerProps {
  scenario?: UserScenario;
  locations?: LocationWithCoordinates[];
  currentTax?: number;
  onScenarioChange?: (updates: Partial<UserScenario>) => void;
  /** If true, renders its own topbar (standalone mode) */
  standalone?: boolean;
  /** Restore the starting scenario */
  onReset?: () => void;
}

export const TaxExplorer = ({ 
  scenario: externalScenario, 
  locations: externalLocations, 
  currentTax: externalCurrentTax,
  onScenarioChange: externalOnScenarioChange,
  standalone = false,
  onReset,
}: TaxExplorerProps) => {
  // Internal state for standalone mode
  const [internalScenario, setInternalScenario] = useState<UserScenario>(defaultScenario);
  const [activeCategories, setActiveCategories] = useState<string[]>([]);
  const { locations: fetchedLocations, isLoading } = useLocationsWithCoordinates();
  
  // Use external or internal state
  const scenario = externalScenario ?? internalScenario;
  const locations = externalLocations ?? fetchedLocations;
  const isStandalone = standalone || !externalScenario;
  
  const handleScenarioChange = useCallback((updates: Partial<UserScenario>) => {
    if (externalOnScenarioChange) {
      externalOnScenarioChange(updates);
    } else {
      setInternalScenario(prev => ({ ...prev, ...updates }));
    }
  }, [externalOnScenarioChange]);

  const [pinnedCountries, setPinnedCountries] = useState<number[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);

  const results = useMemo(() => {
    if (locations.length === 0) return [];
    return calculateAllLocations(scenario, locations);
  }, [scenario, locations]);

  const currentTax = useMemo(() => {
    if (externalCurrentTax !== undefined) return externalCurrentTax;
    if (!scenario.currentResidencyId || locations.length === 0) return 0;
    return calculateCurrentTax(scenario, locations);
  }, [scenario, locations, externalCurrentTax]);

  const best = useMemo(() => rankBestForYou(results, 5, scenario.currentResidencyId), [results, scenario.currentResidencyId]);
  const handleReset = useCallback(() => {
    if (onReset) onReset(); else setInternalScenario(defaultScenario);
  }, [onReset]);

  const pinnedResults = useMemo(() => {
    return results.filter(r => pinnedCountries.includes(r.locationId));
  }, [results, pinnedCountries]);

  const handlePinCountry = useCallback((locationId: number) => {
    setPinnedCountries(prev => {
      if (prev.includes(locationId)) {
        return prev.filter(id => id !== locationId);
      }
      if (prev.length >= 5) return prev;
      return [...prev, locationId];
    });
  }, []);

  const handleUnpinCountry = useCallback((locationId: number) => {
    setPinnedCountries(prev => prev.filter(id => id !== locationId));
  }, []);

  if (isStandalone && isLoading) {
    return (
      <div className={cn(SPACING.contentContainer)}>
        <div className="flex gap-2">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-10 w-32" />
          ))}
        </div>
        <div className="w-full h-[550px] rounded-lg border bg-muted/30 flex items-center justify-center">
          <div className={cn(SPACING.listItemGap, 'text-center')}>
            <Skeleton className="h-12 w-12 rounded-full mx-auto" />
            <Skeleton className="h-4 w-32 mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  const panel = (
    <ScenarioPanel
      scenario={scenario}
      locations={locations}
      currentTax={currentTax}
      onScenarioChange={handleScenarioChange}
      onReset={handleReset}
    />
  );

  return (
    <div className={cn(SPACING.contentContainer, 'pb-20 lg:pb-0')}>
      <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="hidden rounded-lg border border-border bg-card p-4 lg:block lg:self-start lg:sticky lg:top-20">
          {panel}
        </aside>
        <div className="flex min-w-0 flex-col gap-4">
          <div className="w-full h-[460px] lg:h-[520px] rounded-lg overflow-hidden border shadow-sm">
            <Suspense fallback={<Skeleton className="w-full h-full" />}>
              <TaxExplorerMap
                locations={locations}
                results={results}
                pinnedCountries={pinnedCountries}
                selectedCountry={selectedCountry}
                onSelectCountry={setSelectedCountry}
                onPinCountry={handlePinCountry}
                hasScenario={!!scenario.currentResidencyId}
              />
            </Suspense>
          </div>
          {best.length > 0 && (
            <BestForYouList
              results={best}
              hasResidence={!!scenario.currentResidencyId}
              pinned={pinnedCountries}
              selected={selectedCountry}
              onSelect={setSelectedCountry}
              onPin={handlePinCountry}
            />
          )}
        </div>
      </div>
      {pinnedResults.length > 0 && (
        <TaxExplorerComparison
          results={pinnedResults}
          currentTax={currentTax}
          onUnpinCountry={handleUnpinCountry}
        />
      )}
      {/* Mobile: situation in a bottom sheet */}
      <Sheet>
        <SheetTrigger asChild>
          <Button type="button" className="fixed bottom-4 left-1/2 z-40 -translate-x-1/2 rounded-full shadow-lg lg:hidden">
            <SlidersHorizontal className="h-4 w-4 me-2" aria-hidden="true" />
            <LComponents id="tax_explorer.adjust" defaultValue="Adjust my situation" />
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
          <SheetTitle className="sr-only"><LComponents id="tax_explorer.panel.title" defaultValue="Your situation" /></SheetTitle>
          {panel}
        </SheetContent>
      </Sheet>
    </div>
  );
};
